import React from 'react';
import { LayoutBase } from './base/layout-base';
import { Header } from '../header/header';
import { cn } from '../../../utils/tailwind';

interface LayoutPropsInterface {
    children: React.ReactNode | Array<React.ReactNode>;
    intro?: React.ReactNode;
    header?: React.ReactNode;
    introAppearance?: 'dark' | 'light' | 'subpage';
}

export const Layout: React.FC<LayoutPropsInterface> = ({ children, intro, header, introAppearance = 'dark' }) => {
    const introRef = React.useRef<HTMLDivElement>(null);
    const backdropRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const introElement = introRef.current;
        const backdrop = backdropRef.current;
        if (introAppearance === 'dark' || !introElement || !backdrop) {
            return undefined;
        }

        let frame = 0;
        let fadeDistance = 1;
        const updateOpacity = () => {
            backdrop.style.opacity = String(Math.max(0, 1 - window.scrollY / fadeDistance));
        };
        const updateSize = () => {
            fadeDistance = Math.max(1, introElement.offsetHeight * 0.85);
            updateOpacity();
        };
        const onScroll = () => {
            if (!frame) {
                frame = window.requestAnimationFrame(() => {
                    frame = 0;
                    updateOpacity();
                });
            }
        };
        const observer = new ResizeObserver(updateSize);
        observer.observe(introElement);
        updateSize();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', onScroll);
            window.cancelAnimationFrame(frame);
        };
    }, [introAppearance]);

    return (
        <LayoutBase>
            {!header && <Header inverse={introAppearance === 'dark'} />}
            <div
                data-web-transition-content
                ref={introRef}
                className={cn(
                    !header && 'pt-[9rem]',
                    introAppearance !== 'dark'
                        ? 'relative text-brand-secondary'
                        : "bg-[url('/images/intro/background.jpg')] bg-cover bg-no-repeat text-neutral-white",
                )}
            >
                {introAppearance !== 'dark' && (
                    <div
                        ref={backdropRef}
                        aria-hidden="true"
                        className="pointer-events-none fixed inset-x-0 top-[50svh] -z-[1] hidden aspect-[1586/992] -translate-y-[84%] motion-reduce:absolute vl:block"
                        style={{
                            maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                            WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                        }}
                    >
                        <div
                            className="absolute inset-0 -scale-x-100 vl:bg-[url('/images/marketing/monery-hero-ribbon-v3.png')] bg-center bg-no-repeat opacity-70"
                            style={{
                                backgroundSize: '100% auto',
                                maskImage: 'linear-gradient(to right, black 30%, transparent 55%)',
                            }}
                        />
                        <div
                            className="absolute inset-0 vl:bg-[url('/images/marketing/monery-hero-ribbon-v3.png')] bg-center bg-no-repeat opacity-50"
                            style={{
                                backgroundSize: '100% auto',
                                maskImage:
                                    'linear-gradient(to left, black 10%, transparent 55%), linear-gradient(to bottom, black, transparent 55%)',
                                maskComposite: 'intersect',
                            }}
                        />
                        <div
                            className="absolute inset-0 vl:bg-[url('/images/marketing/monery-hero-ribbon-v3.png')] bg-center bg-no-repeat opacity-20"
                            style={{
                                backgroundSize: '100% auto',
                                maskImage:
                                    'linear-gradient(to right, black, transparent clamp(24px, (100vw - 1288px) / 2, 640px))',
                            }}
                        />
                    </div>
                )}
                {header}
                {intro && intro}
            </div>
            <div data-web-transition-content className="flex-1">
                {children}
            </div>
        </LayoutBase>
    );
};
